import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlay, FiSquare, FiAlertTriangle, FiRefreshCw, FiFileText } from 'react-icons/fi';
import * as api from '../api/api';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const money = (v) => v == null ? '—' : `$${Number(v).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`;

const ControlDeck = () => {
  const navigate = useNavigate();
  const [botStatus, setBotStatus] = useState({ is_running: false });
  const [watchlist, setWatchlist] = useState([]);
  const [localHealth, setLocalHealth] = useState(null);
  const [livePositions, setLivePositions] = useState([]);
  const [openOrders, setOpenOrders] = useState([]);
  const [globalSettings, setGlobalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exitingSymbol, setExitingSymbol] = useState(null);

  const isLocal = process.env.REACT_APP_LOCAL_EXECUTION === 'true';

  const fetchData = useCallback(async () => {
    try {
      const requests = [api.getBotStatus(), api.getWatchlist(), api.getGlobalSettings()];
      if (isLocal) requests.push(api.getLocalHealth());
      const res = await Promise.all(requests);
      setBotStatus(res[0].data);
      setWatchlist(res[1].data || []);
      setGlobalSettings(res[2].data || null);
      const health = res[3]?.data || null;
      if (health) setLocalHealth(health);

      if (isLocal && health?.broker_connected) {
        const [positionsRes, ordersRes] = await Promise.allSettled([
          api.getLivePositions(),
          api.getOpenOrders(),
        ]);
        setLivePositions(positionsRes.status === 'fulfilled' ? (positionsRes.value.data || []) : []);
        setOpenOrders(ordersRes.status === 'fulfilled' ? (ordersRes.value.data || []) : []);
      } else {
        setLivePositions([]);
        setOpenOrders([]);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Execution state could not be loaded.');
    } finally { setLoading(false); }
  }, [isLocal]);

  useEffect(() => {
    fetchData();
    const timer = isLocal ? setInterval(fetchData, 5000) : null;
    return () => timer && clearInterval(timer);
  }, [fetchData, isLocal]);

  const handleToggleBot = () => toast.promise(api.toggleBotStatus(botStatus.is_running), {
    loading: botStatus.is_running ? 'Stopping execution…' : 'Starting execution…',
    success: () => { fetchData(); return botStatus.is_running ? 'Execution stopped.' : 'Execution started.'; },
    error: (e) => e.response?.data?.detail || 'Execution control failed.'
  });

  const handleEmergencyStop = async () => {
    if (!window.confirm('Emergency stop will stop AlgoStock and cancel AlgoStock-owned working orders. Manual IBKR orders and positions are not touched. Continue?')) return;
    toast.promise(api.emergencyStop(), {
      loading: 'Engaging emergency stop…',
      success: (res) => { fetchData(); return `Execution stopped · ${res.data.algostock_orders_cancelled ?? 0} AlgoStock orders cancelled.`; },
      error: (e) => e.response?.data?.detail || 'Emergency stop failed.'
    });
  };

  const handleExitPosition = async (ticker) => {
    if (!window.confirm('Exit AlgoStock-managed ' + ticker + ' in IBKR Paper? The protective stop will be cancelled and reconciled before the market exit is submitted.')) return;
    setExitingSymbol(ticker);
    try {
      const response = await api.liquidatePosition(ticker);
      toast.success(ticker + ' exit submitted · ' + (response.data.exit_order_id || 'pending'));
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || ('Could not safely exit ' + ticker + '.'));
    } finally {
      setExitingSymbol(null);
    }
  };

  if (loading) return <div className="workspace-page"><LoadingSpinner/></div>;

  const brokerReady = Boolean(localHealth?.broker_connected);
  const runtimeReady = Boolean(localHealth?.runtime_alive);
  const paperLocked = !localHealth?.live_unlocked;
  const executionEnabled = globalSettings?.execution_enabled !== false;
  const executionReady = runtimeReady && brokerReady && paperLocked && executionEnabled && !localHealth?.last_error;
  const algoPositions = livePositions.filter(pos => pos.order_id);
  const externalPositions = livePositions.filter(pos => !pos.order_id);
  const algoOrders = openOrders.filter(order => String(order.order_ref || '').startsWith('ALGOSTOCK:'));
  const externalOrders = openOrders.filter(order => !String(order.order_ref || '').startsWith('ALGOSTOCK:'));

  return (
    <div className="workspace-page">
      <div className="page-header">
        <div className="page-header-copy">
          <div className="eyebrow">Execution workspace</div>
          <h1 className="page-title">Execution</h1>
          <p className="page-description">Broker state, safety readiness and frozen-plan campaign management. Local V1 is IBKR-only, paper-locked and never originates a new entry without a Trade Plan.</p>
        </div>
        <div className="page-actions">
          <button className="ws-button" onClick={fetchData}><FiRefreshCw/> Refresh</button>
          <button
            className={`ws-button ${botStatus.is_running ? '' : 'primary'}`}
            onClick={handleToggleBot}
            disabled={!botStatus.is_running && !executionReady}
            title={!botStatus.is_running && !executionReady ? 'Execution readiness checks must pass before Start is enabled.' : undefined}
          >
            {botStatus.is_running ? <><FiSquare/> Stop</> : <><FiPlay/> Start</>}
          </button>
          <button className="ws-button danger" onClick={handleEmergencyStop}><FiAlertTriangle/> Emergency stop</button>
        </div>
      </div>

      <div className="status-strip">
        <div className="status-chip"><span className={`status-dot ${runtimeReady ? 'good':'bad'}`}/><span>Runtime</span><strong>{runtimeReady ? 'ONLINE':'OFFLINE'}</strong></div>
        <div className="status-chip"><span className={`status-dot ${brokerReady ? 'good':'warn'}`}/><span>IBKR</span><strong>{brokerReady ? 'CONNECTED':'NOT CONNECTED'}</strong></div>
        <div className="status-chip"><span className={`status-dot ${paperLocked ? 'good':'bad'}`}/><span>Mode</span><strong>{paperLocked ? 'PAPER LOCKED':'LIVE UNLOCKED'}</strong></div>
        <div className="status-chip"><span className={`status-dot ${botStatus.is_running ? 'good':'warn'}`}/><span>Engine</span><strong>{botStatus.is_running ? 'RUNNING':'STOPPED'}</strong></div>
        <div className="status-chip"><span className="status-dot good"/><span>Ticks</span><strong>{localHealth?.successful_ticks ?? 0} ok / {localHealth?.failed_ticks ?? 0} failed</strong></div>
      </div>

      {!executionReady && (
        <div className="ws-banner warning"><FiAlertTriangle/><div><strong>Execution is not ready.</strong> {brokerReady ? 'Review runtime/safety state before starting.' : 'Connect and test an IBKR Paper account in Settings before starting the engine.'}</div></div>
      )}
      {localHealth?.last_error && <div className="ws-banner danger"><strong>Runtime error:</strong> {localHealth.last_error}</div>}
      {(externalPositions.length > 0 || externalOrders.length > 0) && (
        <div className="ws-banner warning">
          <FiAlertTriangle/>
          <div><strong>External IBKR state detected.</strong> {externalPositions.length} external position(s) and {externalOrders.length} external working order(s) are visible for awareness only. AlgoStock does not own or manage them.</div>
        </div>
      )}

      <div className="execution-hero">
        <section className="ws-panel pad">
          <div className="section-header"><h2 className="section-title">Execution state</h2><span className="section-meta">Fail-closed runtime</span></div>
          <div className="execution-state">
            <span className={`execution-light ${botStatus.is_running ? 'running':''}`}/>
            <div>
              <div className="execution-label">{botStatus.is_running ? 'Engine running' : 'Engine stopped'}</div>
              <div className="row-sub">{botStatus.is_running ? 'AlgoStock is reconciling and managing frozen-plan campaigns.' : 'No plan-linked campaign management is running.'}</div>
            </div>
          </div>
          <div className="readiness-list">
            <div className="readiness-item"><span>Local runtime</span><strong className={runtimeReady ? 'good':'bad'}>{runtimeReady ? 'READY':'NOT READY'}</strong></div>
            <div className="readiness-item"><span>IBKR connection</span><strong className={brokerReady ? 'good':'bad'}>{brokerReady ? 'READY':'REQUIRED'}</strong></div>
            <div className="readiness-item"><span>Live safety lock</span><strong className={paperLocked ? 'good':'bad'}>{paperLocked ? 'LOCKED':'UNLOCKED'}</strong></div>
            <div className="readiness-item"><span>Execution setting</span><strong className={executionEnabled ? 'good':'bad'}>{executionEnabled ? 'ENABLED':'DISABLED'}</strong></div>
            <div className="readiness-item"><span>Runtime errors</span><strong className={!localHealth?.last_error ? 'good':'bad'}>{localHealth?.last_error ? 'REVIEW':'CLEAR'}</strong></div>
          </div>
        </section>

        <section className="ws-panel pad">
          <div className="section-header"><h2 className="section-title">Plan-first execution</h2><span className="section-meta">Frozen intent only</span></div>
          <div className="execution-state">
            <FiFileText />
            <div>
              <div className="execution-label">New entries start from Trade Plans</div>
              <div className="row-sub">Choose a researched setup, freeze trigger, stop, expiry and risk, then explicitly arm paper execution from the Plan.</div>
            </div>
          </div>
          <button className="ws-button primary" onClick={()=>navigate('/trade-plans')}><FiFileText/> Open Trade Plans</button>
        </section>
      </div>

      <div className="section-stack">
        <div className="execution-blotter">
          <section className="blotter-panel">
            <div className="blotter-head"><strong>OPEN POSITIONS</strong><span>{algoPositions.length} AlgoStock · {externalPositions.length} external</span></div>
            {livePositions.length ? (
              <div className="ws-table-wrap">
                <table className="terminal-table">
                  <thead><tr><th>Ticker</th><th>Owner</th><th>Qty</th><th>Entry</th><th>Current</th><th>Stop</th><th>U/P&L</th><th>Action</th></tr></thead>
                  <tbody>{livePositions.map(pos => (
                    <tr key={pos.ticker}>
                      <td><strong className="ticker">{pos.ticker}</strong></td>
                      <td><span className={pos.order_id ? 'owner-tag algo' : 'owner-tag external'}>{pos.order_id ? 'ALGOSTOCK' : 'EXTERNAL'}</span></td>
                      <td>{Number(pos.qty || 0).toLocaleString()}</td>
                      <td>{money(pos.avg_entry_price)}</td>
                      <td>{money(pos.current_price)}</td>
                      <td>{(pos.current_stop_price || pos.initial_stop_price) ? money(pos.current_stop_price || pos.initial_stop_price) : '—'}</td>
                      <td className={Number(pos.unrealized_pl) >= 0 ? 'positive' : 'negative'}>{money(pos.unrealized_pl)}</td>
                      <td>
                        {pos.order_id
                          ? <button
                              className="ws-button danger"
                              onClick={()=>handleExitPosition(pos.ticker)}
                              disabled={exitingSymbol === pos.ticker}
                            >{exitingSymbol === pos.ticker ? 'Exiting…' : 'Exit position'}</button>
                          : <span className="row-sub">External · unmanaged</span>}
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            ) : <div className="blotter-empty">{brokerReady ? 'No AlgoStock-managed open positions.' : 'Connect IBKR Paper to load broker positions.'}</div>}
          </section>

          <section className="blotter-panel">
            <div className="blotter-head"><strong>WORKING ORDERS</strong><span>{algoOrders.length} AlgoStock · {externalOrders.length} external</span></div>
            {openOrders.length ? (
              <div className="ws-table-wrap">
                <table className="terminal-table">
                  <thead><tr><th>Symbol</th><th>Owner</th><th>Side</th><th>Type</th><th>Qty</th><th>Stop</th><th>Status</th></tr></thead>
                  <tbody>{openOrders.map(order => (
                    <tr key={order.order_id}>
                      <td><strong className="ticker">{order.symbol || '—'}</strong></td>
                      <td><span className={String(order.order_ref || '').startsWith('ALGOSTOCK:') ? 'owner-tag algo' : 'owner-tag external'}>{String(order.order_ref || '').startsWith('ALGOSTOCK:') ? 'ALGOSTOCK' : 'EXTERNAL'}</span></td>
                      <td>{order.action || '—'}</td>
                      <td>{order.order_type || '—'}</td>
                      <td>{Number(order.quantity || 0).toLocaleString()}</td>
                      <td>{order.stop_price ? money(order.stop_price) : '—'}</td>
                      <td>{order.status || '—'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            ) : <div className="blotter-empty">{brokerReady ? 'No working broker orders.' : 'Connect IBKR Paper to load working orders.'}</div>}
          </section>
        </div>

        <section className="ws-panel pad">
          <div className="section-header"><h2 className="section-title">Plan-linked campaign rows</h2><span className="section-meta">{watchlist.length} symbols</span></div>
          {watchlist.length ? (
            <div className="watchlist-grid">
              {watchlist.map(item => (
                <article className="watch-card" key={item.id}>
                  <div className="watch-top">
                    <div><div className="ticker" style={{fontSize:'1rem'}}>{item.ticker}</div><div className="watch-status">{item.status || 'Pending'}</div></div>
                  </div>
                  <div className="watch-meta">
                    <div><span>ATR</span><strong>{item.atr ? Number(item.atr).toFixed(2) : '—'}</strong></div>
                    <div><span>Max position</span><strong>{item.max_pos_percent ? `${item.max_pos_percent}%` : 'Frozen plan / global cap'}</strong></div>
                  </div>
                  <div className="strategy-tags">
                    <span className="row-sub">New entry intent is controlled by the frozen Trade Plan, not by editable watchlist strategies.</span>
                  </div>
                </article>
              ))}
            </div>
          ) : <div className="empty-state"><strong>No plan-linked campaigns</strong>Freeze a Trade Plan first; a campaign row is created when paper execution is explicitly armed.</div>}
        </section>
      </div>
    </div>
  );
};

export default ControlDeck;
